import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Order } from '@runninglime/shared';
import { apiGet } from '../../lib/api.ts';
import { formatPrice, formatDate } from '../../lib/utils.ts';
import { StatusBadge, LoadingSpinner, EmptyState } from '../../components/ui/Shared.tsx';

export default function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const res = await apiGet<{ items: Order[]; total: number }>('/orders');
      if (res.success && res.data) {
        setOrders(res.data.items);
      }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-dark">My Orders</h1>
          <p className="text-gray-500 mt-1">Track and manage your orders</p>
        </div>
      </div>

      {orders.length === 0 ? (
        <EmptyState
          icon="📦"
          title="No orders yet"
          description="Place your first order from our B2B store"
          action={
            <Link to="/store" className="btn-gradient px-6 py-3 rounded-xl inline-block">
              Browse Store
            </Link>
          }
        />
      ) : (
        <div className="space-y-4 stagger-children">
          {orders.map((order) => (
            <Link
              key={order.id}
              to={`/orders/${order.id}`}
              className="block bg-white rounded-2xl p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 border-l-4 border-lime"
            >
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-mono text-sm text-gray-500">#{order.id.slice(0, 8)}</span>
                    <StatusBadge status={order.status} />
                  </div>
                  <p className="text-sm text-gray-500">
                    {order.items.length} item{order.items.length !== 1 && 's'} · {formatDate(order.createdAt)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-dark">{formatPrice(order.total)}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
